import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from "react";
import Moveable from "react-moveable";
import { useDispatch, useSelector } from "react-redux";
import {
  getActiveObject,
  getActiveObjectprops,
  getActiveObjects,
  getCurrentActivePageObjects,
  getAllObjectsOfAllLayoutsOfCurrentPage,
  getZoom,
  getDragger,
  getCanvasSize,
  getCanvasScale,
} from "../../library/utils/helpers";
import {
  setCurrentObjectProperties,
  deSelectActiveObject,
  updateMultipleObjects,
  removeMultipleObjectsInPage,
  selectAllObjects,
  setActiveObject,
} from "../../store/slices/canvas";
import { flushSync } from "react-dom";
import ImageSettings from "../popups/ImageSettingsPopover";
import ObjectProperty from "../popups/ObjectPropertiesPopover";
import { createPortal } from "react-dom";
import MultiSelectToolbar from "../popups/MultiSelectToolbar";
import { Controls } from "three";
import { TbZoomMoney } from "react-icons/tb";
import { USER_TYPES } from "../../library/utils/constants/index.js";
import { applyLiveObjectTransform } from "../../library/utils/image/progressiveImage";
import { setLiveResize, clearLiveResize } from "./liveResizeStore";

// Horizontal nudge (px) for the multi-select toolbar so its left edge clears the
// top-centre group rotate handle (they overlapped — the toolbar hid the dragger).
const MULTISELECT_TOOLBAR_X_OFFSET = 40;

const ItemDragger = () => {
  const dispatch = useDispatch();
  const activeObject = useSelector(getActiveObject);
  const currentActiveObject = useSelector(getActiveObjectprops);
  const activeObjects = useSelector(getActiveObjects); // multi-select array
  const zoomRatio = useSelector(getZoom);
  const canvasSize = useSelector(getCanvasSize);
  // The canvas-box uses `transform: scale(canvasScale)` for user-zoom. Moveable
  // lives INSIDE that transform, so its 1px lines get scaled too. Its `zoom` prop
  // is the INVERSE scale: at 50% (scale 0.5) we pass 2 so lines render 2px local →
  // a true 1px on screen (Firefox rounds sub-1px lines to 0 → vanishing borders).
  const canvasScale = useSelector(getCanvasScale);
  const moveableZoom = 1 / (canvasScale || 1);
  const getActivepageObjects = useSelector(getCurrentActivePageObjects);
  // Whole-page object list (BOTH sides). Used ONLY as an effect dep, to notice
  // that a selected object disappeared. getActivepageObjects covers just
  // layout[activeSide], and a pasted clone stays on its SOURCE side, which is
  // often not the active one.
  const allPageObjects = useSelector(getAllObjectsOfAllLayoutsOfCurrentPage);
  //     const dispatch = useDispatch
  const [elementGuidelines, setElementGuidelines] = useState([]);
  const [initialSize, setInitialSize] = useState({ width: 0, height: 0 });
  const [targetWidth, setTargetWidth] = useState("");
  const [targetHeight, setTargetHeight] = useState("");
  const [resizeTranslate, setResizeTranslate] = useState("");
  const [initialPosition, setInitialPosition] = useState({ x: 0, y: 0 });
  const [initialRotation, setInitialRotation] = useState(0);
  const [initialImagePositionX, setInitialImagePositionX] = useState(0);
  const [initialImagePositionY, setInitialImagePositionY] = useState(0);

  const [isUpdatingObject, setIsUpdatingObject] = useState(false);
  const [textFontSize, setTextFontSize] = useState(0);
  const [keepratio, setKeepratio] = useState(false);
  const [defaultTextWidth, setDefaultTextWidth] = useState(0);
  const [defaultFontSize, setDefaultFontSize] = useState(0);
  const moveableRef = useRef(null);
  // Separate ref for the multi-target Moveable so both can coexist
  const groupMoveableRef = useRef(null);
  const isDragger = useSelector(getDragger);
  const [deviceWidth, setDeviceWidth] = useState(0);
  const [deviceHeight, setDeviceHeight] = useState(0);
  const [isAdjustable, setIsAdjustable] = useState(false);
  const [multiSelectToolbarPos, setMultiSelectToolbarPos] = useState({ top: 0, left: 0 });
  const groupResizeSnapshotRef = useRef({});
  // onRender dispatch throttling: apply the visual transform on every frame but
  // coalesce the redux dispatch to one per animation frame. This breaks the
  // flushSync synchronous re-render → re-fire loop ("Maximum update depth") and
  // removes the per-event dispatch flood that caused rotate/drag lag.
  const onRenderRafRef = useRef(null);
  const onRenderLatestRef = useRef(null);
  // True only while a ROTATE gesture is in progress (single object). Redux is
  // frozen during the imperative rotate, so onRender writes the live angle
  // straight to the label's `.labelObjectRotationVal` node.
  const isRotatingRef = useRef(false);
  // True only while a RESIZE gesture is in progress. Drag/rotate are applied
  // imperatively (cssText) and committed to redux once on gesture end, so they
  // dispatch NOTHING per frame (which is what re-rendered the whole MainCanvas
  // ~60x/sec). Resize's size visual comes from redux width/height, so it must
  // keep dispatching during the gesture — gated on this flag.
  const isResizingRef = useRef(false);
  // Imperative (no-redux-per-frame) resize. During the gesture we push the live
  // geometry to the per-object liveResizeStore — only the resized object's
  // renderer re-renders, NOT the heavy MainCanvas (the resize lag was MainCanvas
  // re-rendering synchronously on every per-frame dispatch). Committed to redux
  // ONCE on gesture end. isImperativeResizeRef gates this path (currently images);
  // resizeWorkingRef holds the evolving geometry so the image-cover math chains
  // frame-to-frame the same way the old per-dispatch flushSync chain did (redux is
  // frozen during the gesture, so we can't read the live object as the base).
  const isImperativeResizeRef = useRef(false);
  const resizeWorkingRef = useRef(null);

  // ── Group (multi-select) imperative gesture state ─────────────────────────
  // Same principle as single-object: the per-frame updateMultipleObjects dispatch
  // re-rendered the whole ~3900-line MainCanvas ~60x/sec — that was the group
  // drag/resize lag. Now the gesture is imperative (cssText + footer mirror for
  // drag/rotate; per-member liveResizeStore for resize) and committed to redux
  // ONCE on gesture end.
  const isGroupResizingRef = useRef(false);
  // id -> base rotation (deg) captured at drag/rotate start, so the footer mirror
  // keeps an already-rotated member rotated during a pure drag (Moveable reports
  // rotate=null for drag-only frames).
  const groupBaseRotationRef = useRef({});
  // Map<id, {areaType, x, y, rotation|null}> — latest drag/rotate frame per member.
  const groupDragUpdatesRef = useRef(null);
  // Map<id, {areaType, width, height, image?, fontSize?, x?, y?}> — latest resize
  // frame per member (committed once on end).
  const groupResizeUpdatesRef = useRef(null);

  const getActiveCanvasObjProps = useSelector(getActiveObjectprops);
  const activePageLayouts = useSelector(
    (state) => state.canvas.present.pages?.[state.canvas.present.activePageIndex]?.layout || []
  );
  const getObjects = getActivepageObjects?.length;

  // ── Multi-select mode flag ────────────────────────────────────────────────
  // True when 2+ objects are selected; the single-item toolbars hide themselves
  // by checking activeObject === null.
  const isMultiSelect = activeObjects.length > 1;

  // Look an object's CANVAS element up by id.
  //
  // ⚠️ Scope the query to the canvas svg. `data-id-t` is NOT unique in the
  // document: Footer.jsx stamps the SAME attribute on every footer thumbnail
  // copy (and the 3D preview pages do too). A bare
  // `document.querySelector('g[data-id-t=...]')` returns the FIRST match in
  // document order, so it can hand back a footer thumbnail. Measuring the
  // selection frame against a thumbnail's rect puts the box far from the
  // objects and far from the pointer.
  const canvasElById = (id) => {
    if (!id) return null;
    const svg = document.getElementById("canvasWrapper");
    return (svg || document).querySelector(`g[data-id-t="${id}"]`);
  };

  // Resolve the DOM elements for every selected object (multi-select mode).
  // Use state + useEffect so DOM queries run after React commits new canvas objects
  // (useMemo runs during render, before DOM is updated — pasted objects won't be found).
  const [multiTargets, setMultiTargets] = useState([]);
  useLayoutEffect(() => {
    if (!isMultiSelect) {
      setMultiTargets([]);
      return;
    }
    const targets = activeObjects
      .map(({ id }) => canvasElById(id))
      .filter(Boolean);
    // Keep the SAME array when the resolved nodes are unchanged. This effect
    // re-runs on every allPageObjects change (i.e. every committed edit), and a
    // fresh array each time re-rendered Moveable with a "new" target list, which
    // made it re-measure mid-gesture.
    setMultiTargets((prev) =>
      prev.length === targets.length && prev.every((el, i) => el === targets[i])
        ? prev
        : targets
    );
    // allPageObjects dep: same reason as resolvedSingleTarget below -- undo pins
    // activeObjects, so without it a multi-object paste's group frame outlived
    // the objects it was drawn around.
  }, [isMultiSelect, activeObjects, allPageObjects]);

  // For single selection: activeObject may arrive without a .target (e.g., after
  // a shift-click that collapsed back to one item). Resolve it from the DOM.
  // Use state + useEffect so freshly pasted objects (not yet in DOM during render) are found.
  const [resolvedSingleTarget, setResolvedSingleTarget] = useState(null);
  useLayoutEffect(() => {
    if (!activeObject) {
      setResolvedSingleTarget(null);
      return;
    }
    if (activeObject.target && document.body.contains(activeObject.target)) {
      setResolvedSingleTarget(activeObject.target);
      return;
    }
    if (activeObject.id) {
      const el = canvasElById(activeObject.id);
      setResolvedSingleTarget(el || null);
    }
    // `allPageObjects` is a dep on purpose. Undo PINS activeObject
    // (preservedUIState in store.jsx), so undoing a paste keeps the EXACT same
    // object reference -- with `[activeObject]` alone this never re-ran, so
    // resolvedSingleTarget kept pointing at the removed node and the Moveable
    // frame stayed painted until some unrelated re-render cleared it (the
    // "borders stay for a second after undo" bug). It must be the ALL-LAYOUTS
    // list, not layout[activeSide]: a pasted clone stays on its source side,
    // which is frequently not the active one.
  }, [activeObject, allPageObjects]);
  const [labelObjectPropertyPosition, setLabelObjectPropertyPosition] =
    useState({ top: 0, left: 0 });
  const [isCtrlPressed, setIsCtrlPressed] = useState(false);

  const [rotationPosition, setRotationPosition] = useState("top");
  const [isGoUp, setIsGoUp] = useState(null);
  const users = localStorage.getItem("userDetails");
  const userObj = JSON.parse(users);
  useEffect(() => {
    //  setElementGuidelines(prev => [...prev, document.querySelector(".WrapperDiv")]);
    // leftSide, rightSide class meed to be added to guidelines
    setElementGuidelines((prev) => [
      ...prev,
      document.querySelector(".leftSide"),
    ]);
    setElementGuidelines((prev) => [
      ...prev,
      document.querySelector(".rightSide"),
    ]);
    setElementGuidelines((prev) => [
      ...prev,
      document.querySelector(".page-item"),
    ]);
    setElementGuidelines((prev) => [
      ...prev,
      document.querySelector(".targetE1"),
    ]);

    // set device widht and height
    setDeviceWidth(window.innerWidth);
    setDeviceHeight(window.innerHeight);
  }, []);

  useEffect(() => {
    const targetElement = resolvedSingleTarget;

    // Ghost selection: the selected object is no longer in the DOM. Undo pins
    // activeObject/activeObjects (preservedUIState in store.jsx), so undoing an
    // action that ADDED the selected object (paste, add) leaves the selection
    // pointing at an object that no longer exists and the Moveable frame hangs
    // around until the next click.
    //
    // Do NOT gate on `activeObject.target`: pasteObject sets activeObject to the
    // plain clone from the slice, which carries no .target DOM property at all,
    // so the old guard never fired for a pasted object.
    //
    // Gate on the REDUX fact (object missing from the page), not on the DOM.
    // A DOM miss is a timing fact: addObjectInPage / paste set activeObject in
    // the same commit that creates the node, so a passive effect can run before
    // Canvas has painted `g[data-id-t=...]` and would deselect a brand-new
    // object. Absence from allPageObjects only happens once it is really gone.
    const stillOnPage =
      !activeObject?.id ||
      allPageObjects?.some((o) => o.id === activeObject.id);

    const liveTarget =
      targetElement && document.body.contains(targetElement)
        ? targetElement
        : activeObject?.id
          ? canvasElById(activeObject.id)
          : null;

    if (activeObject && !stillOnPage) {
      moveableRef.current?.destroy();
      moveableRef.current = null;
      dispatch(deSelectActiveObject());
      return;
    }

    // Same cleanup for a MULTI selection. activeObject is null in that mode, so
    // the guard above cannot cover it: undoing a multi-object paste left the
    // pinned activeObjects ids pointing at objects that no longer exist. The
    // group frame unmounts on its own (its `multiTargets.length > 1` render
    // gate), but the dead ids survived -- and copyObject then took its
    // multi-select branch on them (see the preservedUIState note in store.jsx).
    if (
      isMultiSelect &&
      allPageObjects &&
      !activeObjects.some(({ id }) => allPageObjects.some((o) => o.id === id))
    ) {
      dispatch(deSelectActiveObject());
      return;
    }

    if (activeObject && liveTarget && moveableRef.current) {
      updatePopoverPosition();
      updateLabelObjectPropertyPosition();
    }
  }, [activeObject, resolvedSingleTarget, allPageObjects, isMultiSelect, activeObjects]);

  // Re-measure once webfonts finish loading. Text metrics change the moment a
  // real face replaces the fallback, which resizes the text's bounding box --
  // but nothing else re-measures on that event, so Moveable's selection box and
  // drag origin kept the PRE-font geometry. The box then sat offset from the
  // glyphs and dragging felt like it grabbed the wrong place. A refresh "fixed"
  // it because on a reload the fonts are already in the browser cache and the
  // first measurement is taken with the real metrics.

  // Moveable's group targets are DOM nodes captured when the selection was made.
  // A re-render that REPLACES those nodes (undo/redo, a spine toggle that resizes
  // the page, page nav) leaves `multiTargets` holding detached elements, whose
  // ownerSVGElement is null -- Moveable's updateRect then reads `.clientWidth` of
  // null and throws. The single-target path already guards with
  // document.body.contains(); the group path did not.
  const groupTargetsAlive = () =>
    multiTargets.length > 0 &&
    multiTargets.every((el) => el && document.body.contains(el));

  // Re-resolve the selection's CURRENT nodes from the DOM. The objects are still
  // selected (they live in redux); only the elements React rendered them into
  // were replaced, so look them up again by id.
  const resolveGroupTargets = () =>
    activeObjects
      .map(({ id }) => canvasElById(id))
      .filter(Boolean);

  // Refresh the group frame. If the captured nodes went stale, SWAP IN the live
  // ones instead of skipping: merely skipping left the frame frozen at its last
  // measured rect while the objects had moved, so the selection box sat far from
  // the objects (and from the pointer) until the user re-selected. Only when the
  // selection truly has no live nodes left is the refresh dropped.
  const updateGroupRect = () => {
    if (!groupMoveableRef.current || !isMultiSelect) return;
    if (!groupTargetsAlive()) {
      const live = resolveGroupTargets();
      // Nothing left to draw around (objects genuinely gone) -- leave it to the
      // deselect guard above rather than measuring detached nodes.
      if (live.length !== activeObjects.length || live.length < 2) return;
      // State swap re-renders Moveable with the new targets; it measures them
      // itself on that commit, so don't also call updateRect() on stale nodes.
      setMultiTargets((prev) =>
        prev.length === live.length && prev.every((el, i) => el === live[i])
          ? prev
          : live
      );
      return;
    }
    groupMoveableRef.current.updateRect();
  };
  // Read-only: this repositions the overlay, it never dispatches, so no saved
  // design is modified.
  useEffect(() => {
    let cancelled = false;
    const remeasure = () => {
      if (cancelled) return;
      const target = resolvedSingleTarget;
      if (moveableRef.current && target && document.body.contains(target)) {
        moveableRef.current.updateRect();
      }
      updateGroupRect();
    };
    if (typeof document !== "undefined" && document.fonts?.ready) {
      document.fonts.ready.then(() => {
        // One frame later so the browser has reflowed with the new metrics.
        requestAnimationFrame(remeasure);
      }).catch(() => {});
    }
    // `loadingdone` also fires for fonts that arrive AFTER the initial
    // document.fonts.ready settles (the design-font fetch is async).
    const onLoadingDone = () => requestAnimationFrame(remeasure);
    document.fonts?.addEventListener?.("loadingdone", onLoadingDone);
    return () => {
      cancelled = true;
      document.fonts?.removeEventListener?.("loadingdone", onLoadingDone);
    };
  }, [resolvedSingleTarget, isMultiSelect]);

  useEffect(() => {
    const updateMoveable = () => {
      // FIX: Crash on Undo when object is removed
      const targetElement = resolvedSingleTarget;
      if (getActiveCanvasObjProps && moveableRef.current && targetElement && document.body.contains(targetElement)) {
        moveableRef.current.updateRect();
        moveableRef.current.updateTarget();
      }
      // Keep group moveable in sync too
      updateGroupRect();
    };

    const updateAll = () => {
      updateMoveable();
      updateLabelObjectPropertyPosition();
      updatePopoverPosition();
      updateMultiSelectToolbarPosition();
    };

    let rafId;
    const timeoutId = setTimeout(() => {
      rafId = requestAnimationFrame(updateAll);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [getActiveCanvasObjProps, zoomRatio, getActivepageObjects, isMultiSelect, resolvedSingleTarget, multiTargets]);

  // Eliminate the selection-box lag during an inner-image pan. Moveable's box
  // tracks the target's bounding box (which DOES move with the panning photo),
  // but the general update effect re-measures on a setTimeout(10)+rAF throttle —
  // so the box reads the photo's position a frame or two late (the "delay").
  // useLayoutEffect runs synchronously AFTER the photo's DOM update and BEFORE
  // paint, so re-measuring here lands the box in the same frame as the photo —
  // no visible lag. Only while panning (isDragger); onRender bails on isDragger,
  // so updateRect just repositions the overlay (no dispatch, no loop).
  useLayoutEffect(() => {
    const measure = () => {
      const target = resolvedSingleTarget;
      if (moveableRef.current && target && document.body.contains(target)) {
        moveableRef.current.updateRect();
      }
    };
    if (!isDragger) {
      // Pan ended — including a mere click on the pan handle (press+release with
      // no drag). The press briefly showed the GhostImage, which EXPANDS the
      // object's bounding box; on release the ghost is removed and the box must
      // snap back. Re-measure now AND next frame (so it catches the bbox after
      // the ghost is fully removed) — otherwise the selection box stays oversized.
      measure();
      const rafId = requestAnimationFrame(measure);
      return () => cancelAnimationFrame(rafId);
    }
    // During an inner-image pan the photo now moves IMPERATIVELY (no per-frame
    // redux dispatch), so this effect can no longer rely on getActiveCanvasObjProps
    // changing each frame to re-measure. Run a rAF loop while panning so the
    // selection box tracks the moving photo's bounding box every frame. Only
    // ItemDragger/Moveable re-renders here — the heavy MainCanvas does not.
    let rafId;
    const tick = () => {
      measure();
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isDragger, resolvedSingleTarget]);

  // Keep moveable in sync during layout transitions (sidebar / footer toggle, window resize).
  // We store the "update everything" callback in a ref so the ResizeObserver
  // closure always calls the latest version without needing to reconnect on each render.
  const layoutUpdateRef = useRef(null);
  layoutUpdateRef.current = () => {
    const targetElement = resolvedSingleTarget;
    if (moveableRef.current && targetElement && document.body.contains(targetElement)) {
      moveableRef.current.updateRect();
    }
    updateGroupRect();
    updateLabelObjectPropertyPosition();
    updatePopoverPosition();
    updateMultiSelectToolbarPosition();
  };

  useEffect(() => {
    const canvasWrapper = document.querySelector(".centerArea");
    const wrapperDiv = document.querySelector(".WrapperDiv");
    if (!canvasWrapper && !wrapperDiv) return;

    let rafId = null;
    const observer = new ResizeObserver(() => {
      // Debounce via rAF so we don't thrash during continuous transition frames
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => layoutUpdateRef.current?.());
    });
    if (canvasWrapper) observer.observe(canvasWrapper);
    if (wrapperDiv) observer.observe(wrapperDiv);

    // Final sync after the CSS sidebar-transition finishes (.WrapperDiv uses all 0.4s ease)
    const onTransitionEnd = () => requestAnimationFrame(() => layoutUpdateRef.current?.());
    if (wrapperDiv) wrapperDiv.addEventListener("transitionend", onTransitionEnd);

    return () => {
      observer.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
      if (wrapperDiv) wrapperDiv.removeEventListener("transitionend", onTransitionEnd);
    };
  }, []); // ← stable: observer never needs to reconnect

  // Resolve an object from activePageLayouts by id + areaType.
  // Shared by keyboard handler and group transform handler.
  const resolveObjFromLayouts = useCallback((id, areaType) => {
    const arrKey = areaType === "safeArea" ? "safeAreaObjects" : "objects";
    for (const layout of activePageLayouts) {
      if (!layout) continue;
      const obj = layout[arrKey]?.find((o) => o.id === id);
      if (obj) return obj;
    }
    return null;
  }, [activePageLayouts]);

  // Find the SVG object id at a given screen coordinate.
  // Uses elementsFromPoint to pierce through Moveable's overlay divs
  // and locate the actual SVG [data-id-t] element underneath.
  const findObjectIdAtPoint = useCallback((x, y) => {
    const els = document.elementsFromPoint(x, y);
    for (const el of els) {
      const match = el.closest?.("[data-id-t]") || (el.getAttribute?.("data-id-t") ? el : null);
      if (match) return match.getAttribute("data-id-t");
    }
    return null;
  }, []);

  // on delete key press remove the active object
  useEffect(() => {
    const BASE_SIZE = 1920; // Base size for standard movement
    const getMoveBy = (event) => {
      const maxCanvasSize = Math.max(canvasSize.width, canvasSize.height);
      const baseMoveBy =
        maxCanvasSize > BASE_SIZE ? maxCanvasSize / BASE_SIZE : 1;
      let moveBy = baseMoveBy;
      if (event.shiftKey) {
        moveBy = baseMoveBy * 10;
      }
      //console.log("moveBy" + moveBy);
      return moveBy;
    };

    function request(newTransform) {
      const mv = moveableRef.current;
      const target = resolvedSingleTarget;
      // Guard: a programmatic request() on a Moveable whose target was removed
      // or re-rendered (e.g. during the upload swap churn) initializes its drag
      // able with null internal state and throws "Cannot set properties of null
      // (setting 'dist')". Bail if the target isn't a live DOM node, and wrap the
      // calls so a transient bad state can never crash the editor.
      if (!mv || !target || !document.body.contains(target)) return;
      try {
        if (
          Number.isFinite(newTransform.x) &&
          Number.isFinite(newTransform.y)
        ) {
          mv.request("draggable", { x: newTransform.x, y: newTransform.y }, true);
        }
        if (newTransform.rotation) {
          mv.request("rotatable", { rotate: newTransform.rotation }, true);
        }
      } catch (err) {
        // Moveable internal state not ready — ignore rather than crash.
        console.warn("[ItemDragger] moveable request skipped:", err?.message);
      }
    }

    const handleKeyPress = (event) => {
      // console.log("eventrotation", getActiveCanvasObjProps.transform.rotation);
      const key = typeof event.key === "string" ? event.key.toLowerCase() : "";
      const activeElement = document.activeElement;

      const isInputFocused =
        activeElement.tagName === "INPUT" ||
        activeElement.tagName === "TEXTAREA" ||
        activeElement.tagName === "SELECT" ||
        activeElement.isContentEditable;

      // Ctrl+A / Cmd+A => select all objects on current page
      if ((event.ctrlKey || event.metaKey) && key === "a" && !isInputFocused) {
        event.preventDefault();
        dispatch(selectAllObjects());
        return;
      }

      // Display snap lines when Alt key is pressed
      if (event.ctrlKey) {
        setIsCtrlPressed(true);
        // moveableRef.current?.updateRect();
      }
      // Check if an object can be transformed (not locked, not disabled for customer)
      const isEditable = (obj) => {
        if (!obj || obj.locked) return false;
        if (userObj?.userTypeCode === USER_TYPES.CUSTOMER && obj.disabledForClient) return false;
        return true;
      };

      // Build transform updates for all editable multi-selected objects
      const buildMultiUpdates = (transformFn) =>
        activeObjects
          .map(({ id, areaType }) => {
            const obj = resolveObjFromLayouts(id, areaType);
            if (!isEditable(obj)) return null;
            return { id, areaType, transform: transformFn(obj) };
          })
          .filter(Boolean);

      // Arrow keys: move objects
      const isArrow = key === "arrowup" || key === "arrowdown" || key === "arrowleft" || key === "arrowright";
      if (isArrow && !isInputFocused) {
        const moveBy = getMoveBy(event);
        const dx = key === "arrowleft" ? -moveBy : key === "arrowright" ? moveBy : 0;
        const dy = key === "arrowup" ? -moveBy : key === "arrowdown" ? moveBy : 0;

        if (isMultiSelect) {
          event.preventDefault();
          const updates = buildMultiUpdates((obj) => ({
            x: (obj.transform?.x || 0) + dx,
            y: (obj.transform?.y || 0) + dy,
          }));
          if (updates.length) dispatch(updateMultipleObjects({ updates, history: false }));
        } else if (activeObject?.id && isEditable(currentActiveObject)) {
          const rect = moveableRef.current?.getRect?.();
          if (rect) request({ x: rect.left + dx, y: rect.top + dy });
        }
      } else if ((event.ctrlKey || event.metaKey) && !isInputFocused) {
        // Ctrl+[ / Ctrl+] : rotate
        if (key === "[" || key === "]") {
          const rotDelta = key === "[" ? -1 : 1;
          if (isMultiSelect) {
            event.preventDefault();
            const updates = buildMultiUpdates((obj) => ({
              rotation: (obj.transform?.rotation || 0) + rotDelta,
            }));
            if (updates.length) dispatch(updateMultipleObjects({ updates, history: false }));
          } else {
            const rect = moveableRef.current?.getRect?.();
            if (rect) request({ rotation: rect.rotation + rotDelta });
          }
        }
      }
    };
    document.addEventListener("keydown", handleKeyPress);
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [dispatch, activeObject, activeObjects, isMultiSelect, resolveObjFromLayouts, getActiveCanvasObjProps, currentActiveObject, userObj]);

  useEffect(() => {
    // console.log("getObjects", getObjects);
    const newarr = Array.isArray(getActivepageObjects)
  ? getActivepageObjects[getActivepageObjects.length - 1]
  : null;
    //   console.log("newarr", newarr);
    // objTarget_2d8691e9-ef61-4b1f-8c4f-f797ea37287b
    if (newarr?.id) {
      // we have svg and inside it there is objects which need to be added to guidelines
      //lets get object by id and add it to guidelines
      // g tag has class name like objTarget_2d8691e9-ef61-4b1f-8c4f-f797ea37287b, so we can use this to get the object
      // get by class name and add it to guidelines
      let gtag = document.querySelector(`g.${"objTarget1_" + newarr?.id}`);

      if (!elementGuidelines.includes(gtag)) {
        setElementGuidelines((prev) => [...prev, gtag]);

        //setElementGuidelines(prev => [...prev, document.querySelector(".targetE1")]);
        //setElementGuidelines(prev => [...prev, gtag]);
      }
      // const element = document.querySelector(`div[data-id-t="${newarr?.id}"]`);
      // if (!elementGuidelines.includes(element)) {
      //     setElementGuidelines(prev => [...prev, element]);
      // }
    }
  }, [getObjects]);
  const updatePopoverPosition = () => {
    let popover = document.querySelector(".objectSettingsPopover");
    let centerArea = document.querySelector(".centerArea");
    let SidebarWrapper = document.querySelector(".sidebar-wrapper");
    let headerWrapper = document.querySelector("#header");
    let topActionWrapper = document.querySelector("#top-action-mob");
    if (resolvedSingleTarget && popover && centerArea) {
      const targetRect = resolvedSingleTarget.getBoundingClientRect();
      const popoverRect = popover.getBoundingClientRect();
      const centerAreaRect = centerArea.getBoundingClientRect();
      const headerWrapperRect = headerWrapper?.getBoundingClientRect();
      const topActionWrapperRect = topActionWrapper?.getBoundingClientRect();
      let left =
        targetRect.left +
        window.scrollX +
        targetRect.width / 2 -
        popoverRect.width / 2;
      let top = targetRect.top + window.scrollY + targetRect.height + 10;

      // Ensure the popover stays within the center area
      if (left < centerAreaRect.left) {
        left = centerAreaRect.left;
      }
      if (left + popoverRect.width > centerAreaRect.right) {
        left = centerAreaRect.right - popoverRect.width - 10;
      }

      // Ensure the popover stays within the screen height
      if (top + popoverRect.height > window.innerHeight) {
        // set top to the top of the object
        top = currentActiveObject.transform.y * zoomRatio - 10;
      }

      //Ensure the popover stays under the header and top action wrapper
      const headerH = headerWrapperRect?.height || 0;
      const topActionH = topActionWrapperRect?.height || 0;
      if (top < headerH) {
        top = headerH + topActionH + 20;
      }

      // Ensure the popover stays within the screen width in mobile view
      if (window.innerWidth < 760 && left < 0) {
        left = 0;
      }

    }
  };
  const updateLabelObjectPropertyPosition = () => {
    let propertyPopover = document.querySelector(".labelObjectProperty");
    if (resolvedSingleTarget && propertyPopover) {
      const targetRect = resolvedSingleTarget.getBoundingClientRect();
      // const popoverRect = propertyPopover.getBoundingClientRect();
      const left = targetRect.left + window.scrollX + 15;
      const top = targetRect.top + window.scrollY - 28;
      // ⚠️ Bail out when the position is UNCHANGED. This setter used to always
      // store a FRESH object literal, so it re-rendered on every call even when
      // the label had not moved. Callers run it from effects that depend on
      // state this component owns (resolvedSingleTarget, activeObjects, ...), so
      // render -> effect -> setState -> render looped forever while resizing:
      // "Maximum update depth exceeded". A value-equality guard breaks the cycle
      // without changing where the label ends up.
      setLabelObjectPropertyPosition((prev) =>
        prev && prev.left === left && prev.top === top ? prev : { left, top }
      );
    }
  };

  // IMPERATIVE label-position update for use DURING a drag (per frame). Writes the
  // position straight to the DOM instead of setState. The setState version
  // re-renders ItemDragger → Moveable → the portals on EVERY drag frame, and that
  // per-frame React `Commit` is the drag lag. React still owns this element's
  // position via labelObjectPropertyPosition, but we don't setState during the
  // drag (so React never re-renders to overwrite this write), and onDragEnd's
  // updateLabelObjectPropertyPosition() syncs state to the final position.
  const updateLabelImperative = () => {
    const propertyPopover = document.querySelector(".labelObjectProperty");
    if (resolvedSingleTarget && propertyPopover) {
      const targetRect = resolvedSingleTarget.getBoundingClientRect();
      propertyPopover.style.left = `${targetRect.left + window.scrollX + 15}px`;
      propertyPopover.style.top = `${targetRect.top + window.scrollY - 28}px`;
    }
  };

  // Compute the bounding box of all multi-selected elements and position the
  // toolbar just above it so it floats over the entire selection.
  const updateMultiSelectToolbarPosition = useCallback(() => {
    if (!isMultiSelect || multiTargets.length === 0) return;
    let minTop = Infinity, minLeft = Infinity, maxRight = -Infinity;
    multiTargets.forEach((el) => {
      if (!el || !document.body.contains(el)) return;
      const r = el.getBoundingClientRect();
      minTop   = Math.min(minTop,   r.top  + window.scrollY);
      minLeft  = Math.min(minLeft,  r.left + window.scrollX);
      maxRight = Math.max(maxRight, r.right + window.scrollX);
    });
    if (minTop === Infinity) return;
    const top = minTop - 48; // 48 px above the selection
    // Start at the selection centre, then nudge right so the toolbar's left edge
    // clears the top-centre rotate handle (they otherwise overlap — the toolbar
    // covered the group rotate dragger).
    const left =
      minLeft + (maxRight - minLeft) / 2 + MULTISELECT_TOOLBAR_X_OFFSET;
    // Same no-op guard as updateLabelObjectPropertyPosition: always storing a
    // fresh object literal re-rendered on every call and fed the
    // render -> effect -> setState cycle.
    setMultiSelectToolbarPos((prev) =>
      prev && prev.top === top && prev.left === left ? prev : { top, left }
    );
  }, [isMultiSelect, multiTargets]);

  // When multiTargets populates (e.g. after paste), position toolbar and refresh Moveable.
  useEffect(() => {
    if (multiTargets.length > 1) {
      updateMultiSelectToolbarPosition();
      updateGroupRect();
    }
  }, [multiTargets, updateMultiSelectToolbarPosition]);

  // ═══════════════════════════════════════════════════════════════════════════
  // GROUP (MULTI-SELECT) HANDLERS
  // react-moveable fires these when target is an array of elements.
  // Each `events` entry mirrors a single-object event for one target.
  // ═══════════════════════════════════════════════════════════════════════════

  // Spine render offset of a canvas element, read back from the DOM (Canvas
  // stamps `data-spine-dx`, so the rule stays in ONE place -- ./spineOffset).
  // Moveable reports the element's OWN translate, which on a spined full cover
  // is the PAINTED position = stored x + dx. Subtract dx before storing, or the
  // render re-adds it and the object walks one spine-width right per gesture.
  const spineDxOf = (el) => Number(el?.getAttribute?.("data-spine-dx")) || 0;

  // Live visual for drag/rotate group gestures — and the position mirror during a
  // group resize. Imperative: moves the CANVAS member via cssText and mirrors the
  // move to the FOOTER copies; NO per-frame redux dispatch (that dispatch wrote
  // state.pages and re-rendered the entire MainCanvas ~60x/sec = the lag). The
  // final per-member values are stashed in a ref and committed ONCE in
  // onGroupTransformEnd. (Resize's size persists via onResizeGroup's liveResize.)
  const onRenderGroup = useCallback(({ events }) => {
    if (isDragger) return;
    // Handles ONLY drag/rotate. Resize position + size are applied imperatively in
    // onResizeGroup — during a group RESIZE, Moveable's group render can report a
    // stale/zero translate here, which would collapse the members onto each other
    // (text boxes stacked). So bail out entirely while resizing.
    if (isGroupResizingRef.current) return;
    events.forEach(({ target, cssText, transformObject }) => {
      // Move the canvas member imperatively (translate/rotate via cssText).
      target.style.cssText += cssText;
      const id = target.getAttribute("data-id-t");
      if (!id) return;
      const activeObj = activeObjects.find((o) => o.id === id);
      const x = transformObject.translate[0] - spineDxOf(target);
      const y = transformObject.translate[1];
      // rotate is radians when provided; null on a drag-only frame.
      const computedRotation = transformObject.rotate != null
        ? transformObject.rotate * (180 / Math.PI)
        : null;
      // Mirror rotation must never be 0 for an already-rotated member: on a
      // drag-only frame use the captured base rotation.
      const baseRotation = groupBaseRotationRef.current[id] || 0;
      const mirrorRotation = computedRotation != null ? computedRotation : baseRotation;
      // Mirror to the footer copies; the canvas copy is owned by moveable (cssText
      // above), so applyLiveObjectTransform skips it via ownerSVGElement.
      applyLiveObjectTransform(id, x, y, mirrorRotation, target.ownerSVGElement);
      // Record drag/rotate for the end-commit (rotation null → commit {x,y} only).
      groupDragUpdatesRef.current?.set(id, {
        areaType: activeObj?.areaType,
        x,
        y,
        rotation: computedRotation,
      });
    });
  }, [isDragger, activeObjects]);

  // Live multi-resize. Pushes each member's geometry to the per-object
  // liveResizeStore (only that member's renderer reflows — canvas + footer copies;
  // MainCanvas never subscribes, so it does NOT re-render = no lag). NO per-frame
  // redux dispatch; the final values are stashed and committed ONCE on gesture end.
  const onResizeGroup = useCallback(({ events }) => {
    if (isDragger) return;
    events.forEach((ev) => {
      const { target, width, height, drag } = ev;
      const id = target.getAttribute("data-id-t");
      if (!id) return;
      const activeObj = activeObjects.find((o) => o.id === id);
      const snapshot = groupResizeSnapshotRef.current[id];
      const beforeTranslate = drag?.beforeTranslate;
      // beforeTranslate is a PAINTED position (includes the spine render offset).
      // `x` below is the STORED value (offset removed) -- it is what gets
      // committed to redux; the painted value is rebuilt as `x + dxPainted` for
      // the imperative style write. See spineDxOf.
      const dxPainted = spineDxOf(target);
      const x = beforeTranslate ? beforeTranslate[0] - dxPainted : undefined;
      const y = beforeTranslate ? beforeTranslate[1] : undefined;

      // Live override for the store. The renderers shallow-merge ({...raw,...live}),
      // so `font`/`image` here MUST be complete objects (a bare {size} would drop
      // family/weight in the live preview). The commit uses partials (reducer deep-
      // merges), so keep those separate.
      const liveOverride = { width, height };
      let commitFontSize; // size only → reducer deep-merges old font on commit
      let commitImage;

      // ── Text: scale font proportionally ──
      if (
        snapshot?.type === "text" &&
        Number.isFinite(snapshot.fontSize) &&
        snapshot.fontSize > 0 &&
        snapshot.width > 0 &&
        snapshot.height > 0
      ) {
        const widthScale = width / snapshot.width;
        const heightScale = height / snapshot.height;
        const scaleFactor = (widthScale + heightScale) / 2;
        const nextFontSize = Math.max(1, Math.round(snapshot.fontSize * scaleFactor));
        commitFontSize = nextFontSize;
        // Full font object for the live preview (shallow-merge safe).
        if (snapshot.fontObj) liveOverride.font = { ...snapshot.fontObj, size: nextFontSize };
      }

      // ── Image: scale the inner image so it still fills the resized frame ──
      if (snapshot?.type === "img" && snapshot.width > 0 && snapshot.height > 0) {
        const wRatio = width  / snapshot.width;
        const hRatio = height / snapshot.height;
        // Scale image proportionally using the dominant axis
        let newScale = snapshot.imageScale * Math.max(wRatio, hRatio);
        // Guarantee cover — image must never be smaller than the frame
        newScale = Math.max(newScale, width / snapshot.imageNatW, height / snapshot.imageNatH);
        // Scale the pan offsets proportionally so the crop point stays consistent
        const newPosX = snapshot.imagePosX * wRatio;
        const newPosY = snapshot.imagePosY * hRatio;
        commitImage = {
          width:     snapshot.imageNatW,
          height:    snapshot.imageNatH,
          scale:     newScale,
          positionX: newPosX,
          positionY: newPosY,
        };
        liveOverride.image = commitImage;
      }

      // Apply the member's live POSITION imperatively. As the group scales, each
      // member is translated (beforeTranslate) so it keeps its place; without this
      // the members resize in place and overlap (the text-boxes-stacked bug). Use
      // beforeTranslate + the preserved (pre-resize) rotation — EXACTLY what the
      // end-commit writes, so there is no jump on release. onRenderGroup does NOT run
      // during resize (it can report a bad group translate), so this owns position.
      if (x != null && y != null) {
        const rot = snapshot?.rotation || 0;
        // Canvas element is painted at stored x + its own offset.
        target.style.transform = `translate(${x + dxPainted}px, ${y}px) rotate(${rot}deg)`;
        // Mirror to the footer copies (the canvas copy is skipped via ownerSVGElement);
        // pass the STORED x -- each copy re-adds its own data-spine-dx.
        applyLiveObjectTransform(id, x, y, rot, target.ownerSVGElement);
      }

      // Push live geometry (size + font/image reflow) — NO redux dispatch (that
      // per-frame dispatch was the lag). MainCanvas never subscribes to the store.
      setLiveResize(id, liveOverride);

      // Stash the final values for the single end-commit.
      groupResizeUpdatesRef.current?.set(id, {
        areaType: activeObj?.areaType,
        width,
        height,
        ...(commitImage ? { image: commitImage } : {}),
        ...(commitFontSize != null ? { fontSize: commitFontSize } : {}),
        ...(x != null ? { x } : {}),
        ...(y != null ? { y } : {}),
      });
    });
  }, [isDragger, activeObjects]);

  // Commit the gesture ONCE when it ends — the single history:true checkpoint (the
  // start handlers no longer checkpoint; the gesture was imperative, so `present`
  // is still the pre-gesture state until this commit → one Ctrl+Z fully reverts).
  // The committed values equal the last live frame, so nothing jumps on release.
  const onGroupTransformEnd = useCallback(() => {
    if (isGroupResizingRef.current) {
      const updates = [];
      groupResizeUpdatesRef.current?.forEach((v, id) => {
        updates.push({
          id,
          areaType: v.areaType,
          width: v.width,
          height: v.height,
          ...(v.image ? { image: v.image } : {}),
          ...(v.fontSize != null ? { font: { size: v.fontSize } } : {}),
          // Only x/y — rotation never changes in a resize, and Moveable can report
          // rotate 0 mid-resize; the reducer merges x/y into the existing transform,
          // preserving a rotated member's angle.
          ...((v.x != null || v.y != null)
            ? { transform: { ...(v.x != null ? { x: v.x } : {}), ...(v.y != null ? { y: v.y } : {}) } }
            : {}),
        });
      });
      if (updates.length) {
        dispatch(updateMultipleObjects({ updates, history: true }));
      }
      // Clear the live overrides — the just-committed redux values now equal them,
      // so the renderers stay put (no jump). Dispatch first, then clear.
      activeObjects.forEach(({ id }) => clearLiveResize(id));
      groupResizeUpdatesRef.current = null;
      isGroupResizingRef.current = false;
    } else {
      const updates = [];
      groupDragUpdatesRef.current?.forEach((v, id) => {
        updates.push({
          id,
          areaType: v.areaType,
          // rotation null on a drag-only gesture → commit {x,y} only (reducer keeps
          // the existing rotation); a rotate gesture commits the new angle too.
          transform: v.rotation == null
            ? { x: v.x, y: v.y }
            : { x: v.x, y: v.y, rotation: v.rotation },
        });
      });
      if (updates.length) {
        dispatch(updateMultipleObjects({ updates, history: true }));
      }
      groupDragUpdatesRef.current = null;
    }
    setIsUpdatingObject(false);
    updateMultiSelectToolbarPosition();
  }, [activeObjects, dispatch, updateMultiSelectToolbarPosition]);


  // Drag/rotate group start. Also handles Ctrl/Cmd-click to toggle a member in/out
  // of the selection. No history checkpoint here (onGroupTransformEnd owns the one
  // commit); just capture base rotations for the footer mirror and mark active.
  const onGroupDragRotateStart = useCallback((e) => {
    isGroupResizingRef.current = false;

    // Ctrl/Cmd-click in group mode → toggle object selection (add or remove)
    const inputEvent = e?.inputEvent;
    if (inputEvent && (inputEvent.ctrlKey || inputEvent.metaKey)) {
      const clickedId = findObjectIdAtPoint(inputEvent.clientX, inputEvent.clientY);
      if (clickedId) {
        // Resolve areaType: check if already in selection, else look up from page layouts
        const existing = activeObjects.find((o) => o.id === clickedId);
        let areaType = existing?.areaType || "";
        if (!existing) {
          // Object not in selection — find its areaType from page layouts
          for (const layout of activePageLayouts) {
            if (!layout) continue;
            if (layout.objects?.some((o) => o.id === clickedId)) { areaType = ""; break; }
            if (layout.safeAreaObjects?.some((o) => o.id === clickedId)) { areaType = "safeArea"; break; }
          }
        }
        dispatch(setActiveObject({
          id: clickedId,
          areaType,
          isShiftPressed: true,
        }));
        if (e?.stop) e.stop();
        return;
      }
    }

    // Capture each member's current rotation so the footer mirror keeps rotated
    // members rotated during a pure drag (Moveable reports rotate=null then).
    const rot = {};
    activeObjects.forEach(({ id, areaType }) => {
      const arrKey = areaType === "safeArea" ? "safeAreaObjects" : "objects";
      activePageLayouts.some((layout) => {
        if (!layout) return false;
        const obj = layout[arrKey]?.find((o) => o.id === id);
        if (!obj) return false;
        rot[id] = obj?.transform?.rotation || 0;
        return true;
      });
    });
    groupBaseRotationRef.current = rot;
    groupDragUpdatesRef.current = new Map();
    setIsUpdatingObject(true);
  }, [activeObjects, activePageLayouts, dispatch, findObjectIdAtPoint]);


  // Resize group start. Snapshot baseline dimensions/font/rotation/image-crop so
  // each member scales proportionally without drift between frames. No history
  // checkpoint here — onGroupTransformEnd commits once.
  const onGroupResizeStart = useCallback(() => {
    isGroupResizingRef.current = true;
    const snapshot = {};
    activeObjects.forEach(({ id, areaType }) => {
      const arrKey = areaType === "safeArea" ? "safeAreaObjects" : "objects";
      activePageLayouts.some((layout) => {
        if (!layout) return false;
        const idx = layout[arrKey]?.findIndex((o) => o.id === id);
        if (idx === undefined || idx === -1) return false;
        const obj = layout[arrKey][idx];
        snapshot[id] = {
          type: obj?.type,
          width: Number(obj?.width) || 0,
          height: Number(obj?.height) || 0,
          fontSize: parseFloat(obj?.font?.size) || 0,
          // Full font object so the live override carries family/weight (the store
          // renderer shallow-merges — a bare {size} would drop them mid-resize).
          fontObj: obj?.font || null,
          // Preserved so the footer mirror keeps a rotated member's angle (Moveable
          // can report rotate 0 mid-resize).
          rotation: obj?.transform?.rotation || 0,
          // Image crop state so we can proportionally scale during group resize
          imageScale:  obj?.image?.scale      ?? 1,
          imagePosX:   obj?.image?.positionX   ?? 0,
          imagePosY:   obj?.image?.positionY   ?? 0,
          imageNatW:   obj?.image?.width       ?? 8000,
          imageNatH:   obj?.image?.height      ?? 12000,
        };
        return true;
      });
    });
    groupResizeSnapshotRef.current = snapshot;
    groupResizeUpdatesRef.current = new Map();
    setIsUpdatingObject(true);
  }, [activeObjects, activePageLayouts]);

  // ═══════════════════════════════════════════════════════════════════════════

  const onDrag = ({ target, left, top, moveable }) => {
    // Calculate new position considering the zoom ratio
    if (isDragger === true) {
      return;
    }
    target.style.left = `${left}px`;
    target.style.top = `${top}px`;

    // Imperative (no setState) → the drag no longer re-renders ItemDragger/Moveable
    // every frame (that per-frame `Commit` was the drag lag). Final position is
    // synced to state on onDragEnd.
    updateLabelImperative();
  };

  // After the user interacts with (moves/resizes/rotates) an object, normalize a
  // freshly-placed object's `activeObject` from the PLACEMENT shape (the full
  // object, which carries `type`) to the canvas-SELECTION shape ({ id, areaType }).
  // Why: addObjectInPage leaves activeObject as the full object (with `type`), which
  // the sidebar's replace-vs-add gate treats as "just added → next click adds/fills,
  // don't replace". A normal canvas CLICK normalizes it (setActiveObject sets the
  // no-`type` shape), but dragging a JUST-PLACED image never re-selects it (Moveable
  // is already attached), so it stays in placement shape — and a subsequent sidebar
  // click ADDS a new image instead of REPLACING. This hit optimistic/pending uploads
  // hardest (they're always freshly auto-active). Normalizing on gesture-end makes
  // "I moved/edited this image" count as "selected" → next sidebar click replaces it.
  // Only acts when activeObject has a `type` (i.e. just added/pasted) so it never
  // disturbs already-clicked selections (no `type`) or multi-select (activeObject null).
  // history:false → no extra undo step (the gesture's own commit already made one).
  const normalizeActiveSelectionAfterGesture = () => {
    if (activeObject?.type && activeObject?.id) {
      dispatch(
        setActiveObject({
          id: activeObject.id,
          areaType: activeObject.areaType || "",
          history: false,
        })
      );
    }
  };

  const onDragend = ({ target }) => {
    //console.log("onDragend");
    // setupRotationPosition();
    flushOnRenderNow(); // commit the final throttled transform before the checkpoint
    dispatch(
      setCurrentObjectProperties({
        history: true,
      })
    );
    normalizeActiveSelectionAfterGesture();
    setIsUpdatingObject(false);
    updatePopoverPosition();
    updateLabelObjectPropertyPosition();
  };
  const onDragStart = () => {
    if (currentActiveObject?.locked) {
      return;
    }
    // console.log("onDragStart");
    updatePopoverPosition();
    updateLabelObjectPropertyPosition();
    setIsUpdatingObject(true);
  };

  const getActiveObjectTarget = () => resolvedSingleTarget;
  const onResizeEnd = (e) => {
    // call function to maintain history
    // setupRotationPosition();
    isResizingRef.current = false;

    // ── Imperative resize commit: redux was untouched during the gesture (the
    // live geometry lived in the store). Commit the final width/height/image AND
    // the gesture transform (top/left-anchored resizes moved the wrapper via
    // onRender's cssText) in ONE history:true dispatch, then clear the override —
    // the committed redux value equals the last override, so there is no jump.
    if (isImperativeResizeRef.current) {
      const id = getActiveCanvasObjProps?.id;
      const w = resizeWorkingRef.current;
      const latest = onRenderLatestRef.current;
      if (onRenderRafRef.current != null) {
        cancelAnimationFrame(onRenderRafRef.current);
        onRenderRafRef.current = null;
      }
      onRenderLatestRef.current = null;
      if (w) {
        // Only x/y can change during a resize (top/left-anchored handles move the
        // wrapper). Rotation never changes in a resize, so keep the pre-resize
        // value — from the working ref (seeded from the LIVE object at start, so it
        // reverts on undo), NOT getActiveCanvasObjProps (preserved/stale after undo)
        // and NOT `latest` (Moveable's resize transformObject may report rotate 0,
        // which would wipe a rotated object's angle).
        const transform = latest
          ? {
              ...w.transform,
              x: latest.x - (latest.spineDx || 0),
              y: latest.y,
            }
          : undefined;
        dispatch(
          setCurrentObjectProperties({
            width: w.width,
            height: w.height,
            ...(w.image ? { image: w.image } : {}),
            ...(transform ? { transform } : {}),
            history: true,
          })
        );
      }
      clearLiveResize(id);
      resizeWorkingRef.current = null;
      isImperativeResizeRef.current = false;
      normalizeActiveSelectionAfterGesture();
      updatePopoverPosition();
      updateLabelObjectPropertyPosition();
      setIsUpdatingObject(false);
      return;
    }

    flushOnRenderNow(); // commit the final throttled transform before the checkpoint
    dispatch(
      setCurrentObjectProperties({
        history: true,
      })
    );
    normalizeActiveSelectionAfterGesture();

    updatePopoverPosition();
    updateLabelObjectPropertyPosition();
    setIsUpdatingObject(false);
  };

  const onResize = async (e) => {
    let moveX = e.dist[0];
    let moveY = e.dist[1];

    let newWidth = initialSize.width + moveX;
    let newHeight = initialSize.height + moveY;

    // ── Imperative resize (img/text/shape/sticker): push live geometry to the
    // per-object store, NO redux dispatch (so MainCanvas does not re-render). The
    // image-cover math chains off resizeWorkingRef — redux is frozen during the
    // gesture, so the working ref is the source of truth, reproducing the old
    // per-dispatch flushSync chain. Committed to redux ONCE on gesture end.
    if (isImperativeResizeRef.current && resizeWorkingRef.current) {
      const w = resizeWorkingRef.current;
      let nextImage = w.image;
      let nW = newWidth;
      let nH = newHeight;

      if (w.type === "img" && getActiveCanvasObjProps.url && w.image) {
        const baseImg = w.image;
        const imageMaxWidth =
          baseImg.width * baseImg.scale - Math.abs(baseImg.positionX);
        const imageMaxHeight =
          baseImg.height * baseImg.scale - Math.abs(baseImg.positionY);
        let scaleX = baseImg.scale || 1;
        let scaleY = baseImg.scale || 1;
        let posX = baseImg.positionX;
        let posY = baseImg.positionY;
        if (nW > imageMaxWidth) {
          if (posX < 0) {
            posX = initialImagePositionX + moveX;
            if (posX > 0) posX = 0;
          } else {
            scaleX = nW / baseImg.width;
          }
        }
        if (nH > imageMaxHeight) {
          if (posY < 0) {
            posY = initialImagePositionY + moveY;
            if (posY > 0) posY = 0;
          } else {
            scaleY = nH / baseImg.height;
          }
        }
        const isCorner =
          e.direction && e.direction[0] !== 0 && e.direction[1] !== 0;
        if (isCorner) {
          const newScale = Math.max(scaleX, scaleY);
          nextImage = {
            ...baseImg,
            scale: newScale,
            positionX: posX,
            positionY: posY,
          };
        } else {
          nextImage = { ...baseImg };
          if (moveX > 0) nextImage = { ...nextImage, scale: scaleX, positionX: posX };
          if (moveY > 0) nextImage = { ...nextImage, scale: scaleY, positionY: posY };
        }
      } else if (w.type === "text") {
        // Mirror the old text resize: never shrink the box below the text's
        // measured (wrapped) content height, and keep a sensible minimum width.
        const fontFamily = getActiveCanvasObjProps.font?.family || "Arial";
        const fontSize = parseInt(getActiveCanvasObjProps.font?.size) || 24;
        const fontWeight = getActiveCanvasObjProps.font?.weight || "normal";
        const fontStyle = getActiveCanvasObjProps.font?.style || "normal";
        const measureDiv = document.createElement("div");
        measureDiv.style.cssText = `
          position: absolute;
          visibility: hidden;
          white-space: pre-wrap;
          word-break: break-word;
          font-family: ${fontFamily};
          font-size: ${fontSize}px;
          font-weight: ${fontWeight};
          font-style: ${fontStyle};
          line-height: 1.2;
          width: ${nW}px;
        `;
        measureDiv.textContent = getActiveCanvasObjProps.text || "";
        document.body.appendChild(measureDiv);
        const measuredHeight = measureDiv.offsetHeight;
        document.body.removeChild(measureDiv);
        if (nH < measuredHeight) nH = measuredHeight;
        if (nW < 50) nW = 50;
      }

      resizeWorkingRef.current = {
        ...w,
        width: nW,
        height: nH,
        image: nextImage,
      };
      setLiveResize(getActiveCanvasObjProps.id, {
        width: nW,
        height: nH,
        ...(nextImage ? { image: nextImage } : {}),
      });
      // NOTE: do NOT call updateLabelObjectPropertyPosition() here. It does a
      // getBoundingClientRect() which forces a SYNCHRONOUS layout right after the
      // foreignObject was resized by setLiveResize — a per-frame layout thrash
      // (worst on the first frame = the resize-start "stick"). The label snaps to
      // the final position on commit (onResizeEnd).
      return;
    }

    // console.log("newWidth", initialSize);
    if (getActiveCanvasObjProps.type === "img" && getActiveCanvasObjProps.url) {
      // Ensure the clipping box does not exceed image boundaries
      const imageMaxWidth =
        getActiveCanvasObjProps.image.width *
        getActiveCanvasObjProps.image.scale -
        Math.abs(getActiveCanvasObjProps.image.positionX);
      const imageMaxHeight =
        getActiveCanvasObjProps.image.height *
        getActiveCanvasObjProps.image.scale -
        Math.abs(getActiveCanvasObjProps.image.positionY);

      let currentScale = getActiveCanvasObjProps.image.scale || 1;

      let scaleX = currentScale;
      let scaleY = currentScale;
      let currentPositionX = getActiveCanvasObjProps.image.positionX;
      let currentPositionY = getActiveCanvasObjProps.image.positionY;
      if (newWidth > imageMaxWidth) {
        // check if there position x in minux we can increase it and no need to increase scale
        if (currentPositionX < 0) {
          currentPositionX = initialImagePositionX + moveX;
          if (currentPositionX > 0) {
            currentPositionX = 0;
          }
        } else {
          scaleX = newWidth / getActiveCanvasObjProps.image.width;
        }

        // console.log("scaleX", scaleX);
        //let newScale = newWidth / getActiveCanvasObjProps.image.width;
      }
      if (newHeight > imageMaxHeight) {
        // check if there position x in minux we can increase it and no need to increase scale
        if (currentPositionY < 0) {
          currentPositionY = initialImagePositionY + moveY;
          if (currentPositionY > 0) {
            currentPositionY = 0;
          }
        } else {
          scaleY = newHeight / getActiveCanvasObjProps.image.height;
        }
        //let newScale = newHeight / getActiveCanvasObjProps.image.height;
      }
      // if resizing width than use scaleX and if resizing height than use scaleY

      if (
        (e.direction && e.direction[0] === 1 && e.direction[1] === 1) ||
        (e.direction[0] === -1 && e.direction[1] === 1) ||
        (e.direction[0] === 1 && e.direction[1] === -1) ||
        (e.direction[0] === -1 && e.direction[1] === -1)
      ) {
        // when resizing from top left or top right or bottom left or bottom right
        let newScale = Math.max(scaleX, scaleY);
        dispatch(
          setCurrentObjectProperties({
            image: {
              ...getActiveCanvasObjProps.image,
              scale: newScale,
              positionX: currentPositionX,
              positionY: currentPositionY,
            },
            history: false,
          })
        );
      } else {
        // check move x or y
        if (moveX > 0) {
          // move right

          dispatch(
            setCurrentObjectProperties({
              image: {
                ...getActiveCanvasObjProps.image,
                scale: scaleX,
                positionX: currentPositionX,
              },
              history: false,
            })
          );
        }

        if (moveY > 0) {
          // move bottom
          dispatch(
            setCurrentObjectProperties({
              image: {
                ...getActiveCanvasObjProps.image,
                scale: scaleY,
                positionY: currentPositionY,
              },
              history: false,
            })
          );
        }
      }

      // when move left to right we might need to adjust the position of the image, so top to bottom
    }

    if (getActiveCanvasObjProps.type === "text") {
      // Measure text size to prevent shrinking smaller than content
      const measureDiv = document.createElement("div");
      measureDiv.style.cssText = `
        position: absolute;
        visibility: hidden;
        white-space: pre-wrap;
        word-break: break-word;
        font-family: ${getActiveCanvasObjProps.font?.family || "Arial"};
        font-size: ${parseInt(getActiveCanvasObjProps.font?.size) || 24}px;
        font-weight: ${getActiveCanvasObjProps.font?.weight || "normal"};
        font-style: ${getActiveCanvasObjProps.font?.style || "normal"};
        line-height: 1.2;
        width: ${newWidth}px;
      `;
      measureDiv.textContent = getActiveCanvasObjProps.text || "";
      document.body.appendChild(measureDiv);

      const measuredHeight = measureDiv.offsetHeight;
      const measuredWidth = measureDiv.scrollWidth;
      document.body.removeChild(measureDiv);

      // Also measure with nowrap to get minimum width
      const measureWidthDiv = document.createElement("div");
      measureWidthDiv.style.cssText = `
        position: absolute;
        visibility: hidden;
        white-space: nowrap;
        font-family: ${getActiveCanvasObjProps.font?.family || "Arial"};
        font-size: ${parseInt(getActiveCanvasObjProps.font?.size) || 24}px;
        font-weight: ${getActiveCanvasObjProps.font?.weight || "normal"};
        font-style: ${getActiveCanvasObjProps.font?.style || "normal"};
        line-height: 1.2;
      `;
      // Get the longest line for minimum width
      const lines = (getActiveCanvasObjProps.text || "").split("\n");
      let maxLineWidth = 0;
      lines.forEach(line => {
        measureWidthDiv.textContent = line || " ";
        document.body.appendChild(measureWidthDiv);
        maxLineWidth = Math.max(maxLineWidth, measureWidthDiv.offsetWidth);
        document.body.removeChild(measureWidthDiv);
      });

      // Prevent shrinking below text size (with some padding)
      const minWidth = 50; // Minimum width regardless of text
      const minHeight = parseInt(getActiveCanvasObjProps.font?.size) * 1.2 + 10; // At least one line height

      // Don't allow resize if new size would be smaller than measured content
      if (newHeight < measuredHeight) {
        newHeight = measuredHeight;
      }
      // For width, we don't restrict since text can wrap
      if (newWidth < minWidth) {
        newWidth = minWidth;
      }
    }

    let transformUpdate = {};
    if (getActiveCanvasObjProps.type === "text" && e.drag && e.drag.beforeTranslate) {
      transformUpdate = {
        transform: {
          ...getActiveCanvasObjProps.transform,
          x: e.drag.beforeTranslate[0],
          y: e.drag.beforeTranslate[1],
        },
      };
    }

    dispatch(
      setCurrentObjectProperties({
        width: newWidth,
        height: newHeight,
        ...transformUpdate,
        history: false,
      })
    );
    updateLabelObjectPropertyPosition();
  };

  const onResizeStart = (e) => {
    if (!getActiveCanvasObjProps) return;
    // CRITICAL: seed the gesture from the LIVE object (read from page layouts),
    // NOT from getActiveCanvasObjProps. The undo wrapper (store.jsx) PRESERVES
    // activeObjectprops across Ctrl+Z, so after an undo it still holds the pre-undo
    // (post-resize) geometry while the actual object in `pages` has reverted.
    // Seeding from activeObjectprops made a re-resize start from the stale pre-undo
    // size (jump). The live object reverts on undo, so the next resize starts from
    // the CURRENT size. Falls back to activeObjectprops if the lookup misses.
    const liveObj =
      resolveObjFromLayouts(activeObject?.id, activeObject?.areaType) ||
      getActiveCanvasObjProps;
    if (liveObj.type === "text") {
    } else if (liveObj.type === "img") {
      setInitialImagePositionX(liveObj.image.positionX);
      setInitialImagePositionY(liveObj.image.positionY);
    }
    setInitialSize({
      width: parseFloat(liveObj.width),
      height: parseFloat(liveObj.height),
    });
    isResizingRef.current = true; // resize needs per-frame redux (size visual)
    // img/text/shape/sticker resize imperatively (no per-frame redux → no
    // MainCanvas re-render). Seed the working ref with the current geometry —
    // onResize evolves it and pushes to liveResizeStore. Other types (calendar,
    // qrcode) keep the original redux path.
    isImperativeResizeRef.current = [
      "img",
      "text",
      "shape",
      "sticker",
      "qrcode",
      "calendar",
      "multiple-calendar",
    ].includes(liveObj.type);
    if (isImperativeResizeRef.current) {
      resizeWorkingRef.current = {
        type: liveObj.type,
        width: parseFloat(liveObj.width),
        height: parseFloat(liveObj.height),
        image: liveObj.image ? { ...liveObj.image } : undefined,
        transform: { ...liveObj.transform },
      };
    }
    setIsUpdatingObject(true);
  };
  const onBeforeResize = (e) => {
    if (getActiveCanvasObjProps.type === "text") {
    }
  };

  // rAF callback: commit the latest transform from onRender to redux (once/frame).
  const flushOnRender = () => {
    onRenderRafRef.current = null;
    const latest = onRenderLatestRef.current;
    if (!latest || !getActiveCanvasObjProps) return;
    dispatch(
      setCurrentObjectProperties({
        transform: {
          ...getActiveCanvasObjProps.transform,
          rotation: latest.rotation,
          x: latest.x - (latest.spineDx || 0),
          y: latest.y,
        },
        history: false,
      })
    );
  };

  // Flush any pending throttled transform immediately (used at gesture end so the
  // final position is committed before the history checkpoint).
  const flushOnRenderNow = () => {
    if (onRenderRafRef.current != null) {
      cancelAnimationFrame(onRenderRafRef.current);
      onRenderRafRef.current = null;
    }
    flushOnRender();
    onRenderLatestRef.current = null;
  };

  const onRender = (e) => {
    if (isDragger) {
      return false;
    }
    // Apply the visual transform immediately (smooth), but THROTTLE the redux
    // dispatch to one per animation frame. Dispatching on every Moveable render
    // (with flushSync) re-rendered synchronously and made Moveable re-measure +
    // re-fire onRender within the same tick → "Maximum update depth exceeded"
    // while rotating, and the flood of dispatches was the rotate/drag lag.
    e.target.style.cssText += e.cssText;

    let rotation = 0;
    if (e.transformObject.rotate) {
      rotation = e.transformObject.rotate * (180 / Math.PI);
    }
    onRenderLatestRef.current = {
      rotation,
      x: e.transformObject.translate[0],
      y: e.transformObject.translate[1],
      spineDx: spineDxOf(e.target),
    };
    // Live angle readout during a rotate gesture. Redux is frozen (imperative
    // rotate, committed on end), so ObjectProperty's prop is stale — write the live
    // angle straight to the label node. It re-syncs to the committed value on end.
    if (isRotatingRef.current) {
      const angleEl = document.querySelector(".labelObjectRotationVal");
      if (angleEl) angleEl.textContent = `∠${Math.round(rotation)}°`;
      updateLabelImperative(); // keep the label glued to the object as it rotates
    }
    // Drag/rotate: the cssText above already moved the element imperatively, and
    // the final transform is committed once on gesture end (flushOnRenderNow). So
    // we DON'T dispatch per frame for them — that per-frame dispatch wrote
    // state.pages and re-rendered the entire MainCanvas ~60x/sec (the lag).
    // BUT the footer/preview copies of this object read from redux, so without a
    // dispatch they'd freeze during the gesture. Mirror the move to those copies
    // imperatively (the main canvas copy is moved by the cssText above, so it's
    // skipped) → footer live-preview tracks the drag/rotate with no re-render.
    // Mirror the wrapper's translate to the footer copies for drag/rotate AND for
    // imperative resize (top/left-anchored resizes move the wrapper). The footer's
    // SIZE tracks via the preview renderers' liveResize override; this keeps its
    // POSITION in sync too. Skipped only for the non-imperative redux resize path
    // (that one updates the footer via the per-frame dispatch).
    if (!isResizingRef.current || isImperativeResizeRef.current) {
      // During an imperative resize the rotation never changes, so mirror the
      // object's actual (pre-resize) rotation — NOT the gesture's computed value,
      // which can read 0 for a rotated object being resized and would momentarily
      // un-rotate the footer thumbnail.
      const mirrorRotation = isImperativeResizeRef.current
        ? getActiveCanvasObjProps?.transform?.rotation || 0
        : rotation;
      applyLiveObjectTransform(
        getActiveCanvasObjProps?.id,
        // STORED x (painted minus this element's spine offset) -- each mirrored
        // copy re-adds its OWN data-spine-dx, so the footer keeps its offset.
        e.transformObject.translate[0] - spineDxOf(e.target),
        e.transformObject.translate[1],
        mirrorRotation,
        e.target.ownerSVGElement
      );
    }
    // Resize: ONLY the non-imperative path (text/shape/etc.) needs the per-frame
    // redux dispatch — its size visual comes from redux width/height. For the
    // imperative image path, redux is frozen during the gesture (geometry lives in
    // the store) and the final transform is committed once in onResizeEnd, so we
    // must NOT dispatch here (that would re-render the heavy MainCanvas = the lag).
    // The wrapper's translate is already applied imperatively via the cssText above.
    if (
      isResizingRef.current &&
      !isImperativeResizeRef.current &&
      onRenderRafRef.current == null
    ) {
      onRenderRafRef.current = requestAnimationFrame(flushOnRender);
    }
  };

  //set rotation position

  // const setupRotationPosition = () => {
  //   let canvasWrapper = document.querySelector("#canvasWrapper");
  //   const canvasWrapperRect = canvasWrapper.getBoundingClientRect();

  //   const leftSpace = currentActiveObject.transform.x * zoomRatio;
  //   const rightSpace =
  //     canvasWrapperRect.width -
  //     (currentActiveObject.transform.x * zoomRatio +
  //       currentActiveObject.width * zoomRatio);
  //   const rotationAngle = currentActiveObject.transform.rotation;
  //   const normalizedAngle = ((rotationAngle % 360) + 360) % 360;
  //   if (currentActiveObject.transform.y * zoomRatio < 0) {
  //     setIsGoUp(true);
  //   }
  //   if (currentActiveObject.transform.y * zoomRatio > 0) {
  //     setIsGoUp(false);
  //   }

  //   // // set rotation position
  //   setRotationPosition("top");
  //   const rotationValues = {
  //     "left-top": "left-top",
  //     top: "top",
  //     "right-top": "right-top",
  //     left: "left",
  //     right: "right",
  //     "left-bottom": "left-bottom",
  //     bottom: "bottom",
  //     "right-bottom": "right-bottom",
  //   };
  //   if (normalizedAngle >= 46 && normalizedAngle < 136) {
  //     rotationValues["left-top"] = "bottom-left";
  //     rotationValues.top = "left";
  //     rotationValues["right-top"] = "top-left";
  //     rotationValues.left = "bottom";
  //     rotationValues.right = "top";
  //     rotationValues["left-bottom"] = "bottom-right";
  //     rotationValues.bottom = "right";
  //     rotationValues["right-bottom"] = "top-right";
  //   }

  //   if (normalizedAngle >= 136 && normalizedAngle < 226) {
  //     rotationValues["left-top"] = "right-bottom";
  //     rotationValues.top = "bottom";
  //     rotationValues["right-top"] = "left-bottom";
  //     rotationValues.left = "right";
  //     rotationValues.right = "left";
  //     rotationValues["left-bottom"] = "right-top";
  //     rotationValues.bottom = "top";
  //     rotationValues["right-bottom"] = "left-top";
  //   }

  //   if (normalizedAngle >= 226 && normalizedAngle < 316) {
  //     rotationValues["left-top"] = "top-right";
  //     rotationValues.top = "right";
  //     rotationValues["right-top"] = "bottom-right";
  //     rotationValues.left = "top";
  //     rotationValues.right = "bottom";
  //     rotationValues["left-bottom"] = "top-left";
  //     rotationValues.bottom = "left";
  //     rotationValues["right-bottom"] = "bottom-left";
  //   }

  //   if (
  //     Math.abs(currentActiveObject.transform.y * zoomRatio) >
  //     (currentActiveObject.height / 2) * zoomRatio
  //   ) {
  //     if (!isGoUp) {
  //       if (
  //         currentActiveObject.transform.x * zoomRatio >
  //         (currentActiveObject.width / 2) * zoomRatio
  //       ) {
  //         setRotationPosition(rotationValues["left-top"]);
  //       } else if (
  //         Math.abs(currentActiveObject.transform.x * zoomRatio) >
  //         (currentActiveObject.width / 2) * zoomRatio
  //       ) {
  //         setRotationPosition(rotationValues["right-top"]);
  //       } else {
  //         setRotationPosition(rotationValues["top"]);
  //       }
  //     } else if (rightSpace > leftSpace) {
  //       setRotationPosition(
  //         rotationValues[`right-${isGoUp ? "bottom" : "top"}`]
  //       );
  //     } else if (leftSpace > rightSpace) {
  //       setRotationPosition(
  //         rotationValues[`left-${isGoUp ? "bottom" : "top"}`]
  //       );
  //     }
  //   } else if (currentActiveObject.transform.y * zoomRatio < 50) {
  //     if (!isGoUp) {
  //       if (
  //         currentActiveObject.transform.x * zoomRatio >
  //         (currentActiveObject.width / 2) * zoomRatio
  //       ) {
  //         setRotationPosition(rotationValues["left-top"]);
  //       } else if (
  //         Math.abs(currentActiveObject.transform.x * zoomRatio) >
  //         (currentActiveObject.width / 2) * zoomRatio
  //       ) {
  //         setRotationPosition(rotationValues["right-top"]);
  //       } else {
  //         setRotationPosition(rotationValues["top"]);
  //       }
  //     } else if (rightSpace > leftSpace) {
  //       setRotationPosition(rotationValues["right"]);
  //     } else if (leftSpace > rightSpace) {
  //       setRotationPosition(rotationValues["left"]);
  //     } else if (rightSpace === leftSpace) {
  //       setRotationPosition(rotationValues["top"]);
  //     }
  //   } else if (
  //     currentActiveObject.transform.y * zoomRatio +
  //       currentActiveObject.height * zoomRatio >
  //     canvasWrapperRect.height
  //   ) {
  //     if (!isGoUp) {
  //       if (
  //         currentActiveObject.transform.x * zoomRatio >
  //         (currentActiveObject.width / 2) * zoomRatio
  //       ) {
  //         setRotationPosition(rotationValues["left-top"]);
  //       } else if (
  //         Math.abs(currentActiveObject.transform.x * zoomRatio) >
  //         (currentActiveObject.width / 2) * zoomRatio
  //       ) {
  //         setRotationPosition(rotationValues["right-top"]);
  //       } else {
  //         setRotationPosition(rotationValues["top"]);
  //       }
  //     } else if (rightSpace > leftSpace) {
  //       setRotationPosition(rotationValues["right-top"]);
  //     } else if (leftSpace > rightSpace) {
  //       setRotationPosition(rotationValues["left-top"]);
  //     } else if (rightSpace === leftSpace) {
  //       setRotationPosition(rotationValues["top"]);
  //     }
  //   }
  // };

  // set adjustable
  useEffect(() => {
    //  check user type is customer
    if (userObj?.userTypeCode === USER_TYPES.CUSTOMER) {
      // check current active object is disabled for client
      if (currentActiveObject?.disabledForClient) {
        setIsAdjustable(false);
      } else if (currentActiveObject?.locked) {
        setIsAdjustable(false);
      } else {
        setIsAdjustable(true);
      }
    } else if (currentActiveObject?.locked) {
      setIsAdjustable(false);
    } else {
      setIsAdjustable(true);
    }
  }, [currentActiveObject]);

  return (
    <>
      {/* ── Single-object Moveable ─────────────────────────────────────────── */}
      {/* Only active when exactly 0 or 1 object is selected (isMultiSelect = false). */}
      {!isMultiSelect && resolvedSingleTarget && document.body.contains(resolvedSingleTarget) && (
        <Moveable
          ref={moveableRef}
          zoom={moveableZoom}
          target={resolvedSingleTarget}
          draggable={isAdjustable}
          triggerAblesSimultaneously={true}
          rotatable={isAdjustable}
          resizable={isAdjustable}
          scalable={isAdjustable}
          svgOrigin="50% 50%"
          edge={false}
          origin={false}
          throttleResize={0}
          throttleRotate={0}
          rotationPosition={["left-top", "top", "bottom", "right-bottom"]}
          className="moveable-rotation-control"
          onDragStart={() => onDragStart()}
          onDrag={onDrag}
          onDragEnd={onDragend}
          onRotateStart={(e) => {
            isRotatingRef.current = true;
            setIsUpdatingObject(true);
          }}
          onRotateEnd={(e) => {
            // call function to maintain history
            // setupRotationPosition();
            isRotatingRef.current = false;
            flushOnRenderNow(); // commit the final throttled rotation before the checkpoint

            dispatch(
              setCurrentObjectProperties({
                history: true,
              })
            );
            normalizeActiveSelectionAfterGesture();
            setIsUpdatingObject(false);
          }}
          onResize={onResize}
          onBeforeResize={onBeforeResize}
          onResizeStart={onResizeStart}
          onResizeEnd={onResizeEnd}
          onRender={onRender}
          snappable={true}
          snapThreshold={1}
          snapRenderThreshold={1}
          // Snap-distance digit display OFF: Moveable sizes the digit TEXT as
          // fontSize × zoom, and our inverse `zoom` prop (needed to keep the 1px
          // control lines crisp under the canvas-box transform) blows the digits
          // up huge when zoomed out. Snapping/guidelines still work — only the
          // numeric labels are hidden.
          isDisplaySnapDigit={false}
          isDisplayInnerSnapDigit={false}
          snapGap={true}
          snapDirections={{
            top: true,
            right: true,
            bottom: true,
            left: true,
            center: true,
            middle: true,
          }}
          elementSnapDirections={{
            top: true,
            left: true,
            bottom: true,
            right: true,
            center: true,
            middle: true,
          }}
          snapDigit={0}
          flushSync={flushSync}
          isDisplayGridGuidelines={true}
          elementGuidelines={elementGuidelines}
        />
      )}

      {/* ── Multi-object Moveable ─────────────────────────────────────────── */}
      {/* Rendered only when 2+ objects are selected; handles group transforms. */}
      {isMultiSelect && multiTargets.length > 1 && (
        <Moveable
          ref={groupMoveableRef}
          zoom={moveableZoom}
          target={multiTargets}
          draggable={true}
          rotatable={true}
          resizable={true}
          keepRatio={false}
          triggerAblesSimultaneously={true}
          origin={false}
          edge={false}
          throttleResize={0}
          throttleRotate={0}
          // Match the single-object Moveable: rotate handles at all four corners/
          // edges (default is a single top handle, which the user found lacking).
          rotationPosition={["left-top", "top", "bottom", "right-bottom"]}
          className="moveable-rotation-control"
          snappable={true}
          snapThreshold={1}
          // see single-object Moveable: digits balloon under the inverse zoom prop.
          isDisplaySnapDigit={false}
          isDisplayInnerSnapDigit={false}
          snapGap={true}
          snapDirections={{ top: true, right: true, bottom: true, left: true, center: true, middle: true }}
          elementSnapDirections={{ top: true, left: true, bottom: true, right: true, center: true, middle: true }}
          snapDigit={0}
          flushSync={flushSync}
          elementGuidelines={elementGuidelines}
          // Drag/rotate live sync
          onRenderGroup={onRenderGroup}
          // Resize must persist width/height per target
          onResizeGroup={onResizeGroup}
          // Lifecycle hooks — mark history checkpoint on gesture end
          onDragGroupStart={onGroupDragRotateStart}
          onDragGroupEnd={onGroupTransformEnd}
          onResizeGroupStart={onGroupResizeStart}
          onResizeGroupEnd={onGroupTransformEnd}
          onRotateGroupStart={onGroupDragRotateStart}
          onRotateGroupEnd={onGroupTransformEnd}
        />
      )}

      {/* ── Multi-select floating toolbar ─────────────────────────────────── */}
      {isMultiSelect &&
        createPortal(
          <MultiSelectToolbar
            activeObjects={activeObjects}
            style={{
              top:  `${multiSelectToolbarPos.top}px`,
              left: `${multiSelectToolbarPos.left}px`,
              visibility: !isUpdatingObject ? "visible" : "hidden",
            }}
          />,
          document.body
        )}

      {!isDragger &&
        getActiveCanvasObjProps &&
        createPortal(
          <ObjectProperty
            style={{
              left: `${labelObjectPropertyPosition.left}px`,
              top: `${labelObjectPropertyPosition.top}px`,
            }}
            objectWidth={getActiveCanvasObjProps.width}
            objectHeight={getActiveCanvasObjProps.height}
            objectRotation={getActiveCanvasObjProps.transform?.rotation}
          />,
          document.body
        )}

      {/* Image popover hidden — replaced by ImageFloatingToolbar in layout toolbar slot */}
      {/* {!isDragger &&
        getActiveCanvasObjProps &&
        getActiveCanvasObjProps?.type === "img" &&
        !getActiveCanvasObjProps?.isProcessing &&
        activeObject?.target && document.body.contains(activeObject.target)
        &&
        createPortal(
          <ImageSettings
            style={{
              left: `${popoverPosition.left}px`,
              top: `${popoverPosition.top}px`,
              visibility: !isUpdatingObject ? "visible" : "hidden",
            }}
            onChanges={() => {
              updatePopoverPosition();
            }}
          />,
          document.body
        )} */}

      {/* Sticker popover replaced by StickerFloatingToolbar in layout toolbar slot */}
      {/* Shape popover replaced by ShapeFloatingToolbar in layout toolbar slot */}
    </>
  );
};

export default ItemDragger;
